import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import { styled } from "@mui/system";
import {
  Box,
  ClickAwayListener,
  List,
  Collapse,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
} from "@mui/material";

const ListItemStyle = styled(({ collapsed, ...props }: any) => (
  <ListItemButton disableGutters {...props} />
))(({ collapsed }: any) => ({
  boxSizing: "border-box",
  minHeight: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  textTransform: "capitalize",
  color: "rgba(255,255,255,0.78)",
  borderRadius: 10,
  padding: collapsed ? 0 : "0 12px",
  margin: collapsed ? "4px auto" : "4px 12px",
  width: collapsed ? 44 : "calc(100% - 24px)",
  minWidth: collapsed ? 44 : undefined,
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
  },
}));

const iconWrap = (icon: any, collapsed: boolean, size = 22) => (
  <Box
    sx={{
      width: size,
      height: size,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      mr: collapsed ? 0 : 1.25,
    }}
  >
    {icon}
  </Box>
);

function isPathActive(pathname: string, path?: string) {
  return path ? !!matchPath({ path, end: false }, pathname) : false;
}

function NavItem({ item, pathname, collapsed }: any) {
  const { title, path, icon, info, children } = item;
  const isActiveRoot = children
    ? children.some((child: any) => isPathActive(pathname, child.path))
    : isPathActive(pathname, path);
  const [open, setOpen] = useState(isActiveRoot);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (isActiveRoot) setOpen(true);
  }, [isActiveRoot]);

  const activeRootStyle = {
    color: "#0B3A5B",
    fontWeight: 700,
    bgcolor: "#FFFFFF",
    "&:hover": {
      bgcolor: "#FFFFFF",
    },
  };

  const label = !collapsed && (
    <ListItemText
      primary={title}
      primaryTypographyProps={{
        noWrap: true,
        sx: {
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: "inherit",
        },
      }}
      sx={{ m: 0 }}
    />
  );

  const renderChild = (child: any, inPopover = false) => {
    const isActiveSub = isPathActive(pathname, child.path);
    return (
      <ListItemStyle
        key={child.title}
        component={RouterLink}
        to={child.path}
        collapsed={false}
        onClick={() => setAnchorEl(null)}
        sx={{
          pl: inPopover ? 1.5 : 4.5,
          width: inPopover ? "auto" : undefined,
          mx: inPopover ? 0.75 : undefined,
          color: isActiveSub ? "#fff" : "rgba(255,255,255,0.78)",
          fontWeight: isActiveSub ? 700 : 500,
          bgcolor: isActiveSub ? "rgba(255,255,255,0.12)" : "transparent",
        }}
      >
        {child.icon && iconWrap(child.icon, false, 18)}
        <ListItemText
          primary={child.title}
          primaryTypographyProps={{
            noWrap: true,
            sx: { fontSize: 13, lineHeight: "18px" },
          }}
          sx={{ m: 0 }}
        />
      </ListItemStyle>
    );
  };

  if (children) {
    return (
      <>
        <Tooltip title={collapsed ? title : ""} placement="right">
          <ListItemStyle
            collapsed={collapsed}
            onClick={(event: any) => {
              if (collapsed) {
                setAnchorEl((current) => (current ? null : event.currentTarget));
                return;
              }
              setOpen((prev: any) => !prev);
            }}
            sx={isActiveRoot ? activeRootStyle : undefined}
          >
            {iconWrap(icon, collapsed)}
            {label}
            {!collapsed && info}
            {!collapsed && (
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: "auto" }}
              />
            )}
          </ListItemStyle>
        </Tooltip>

        <Collapse in={open && !collapsed} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child: any) => renderChild(child))}
          </List>
        </Collapse>

        <Popper
          open={collapsed && Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right-start"
          style={{ zIndex: 1400 }}
        >
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Paper
              sx={{
                ml: 1,
                py: 0.75,
                minWidth: 188,
                bgcolor: "#0B3A5B",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
              }}
            >
              <TypographyLabel>{title}</TypographyLabel>
              {children.map((child: any) => renderChild(child, true))}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  return (
    <Tooltip title={collapsed ? title : ""} placement="right">
      <ListItemStyle
        collapsed={collapsed}
        component={RouterLink}
        to={path}
        sx={isActiveRoot ? activeRootStyle : undefined}
      >
        {iconWrap(icon, collapsed)}
        {label}
      </ListItemStyle>
    </Tooltip>
  );
}

const TypographyLabel = ({ children }: { children: any }) => (
  <Box
    sx={{
      px: 1.75,
      pt: 0.5,
      pb: 0.75,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "rgba(255,255,255,0.45)",
    }}
  >
    {children}
  </Box>
);

NavSection.propTypes = {
  navConfig: PropTypes.array,
  collapsed: PropTypes.bool,
};

export default function NavSection({ navConfig, collapsed = false }: any) {
  const { pathname } = useLocation();

  return (
    <List disablePadding sx={{ px: 0 }}>
      {navConfig.map((item: any) => (
        <NavItem
          key={item.title}
          item={item}
          pathname={pathname}
          collapsed={collapsed}
        />
      ))}
    </List>
  );
}
